/**
 * Create/delete thread requests. Navigation and cache invalidation stay in
 * each app's own mutation hook (they differ per app's router/query client);
 * this is just the request the hook wraps.
 */

import type { ChatAuth } from "./chat-fetch";
import { makeChatFetch } from "./chat-fetch";
import { type ChatThread, type EngineThread, type ThreadId, toChatThread } from "./thread";

export interface CreateChatThreadInput {
  title?: string | null;
  model?: string;
  /** Which `agents.roles` agent answers. The engine falls back to `chat` for
   * anything it does not recognise, so an unknown value degrades rather than
   * failing the thread. */
  role?: string;
}

export async function createChatThread(
  auth: ChatAuth,
  baseUrl: string,
  input: CreateChatThreadInput = {},
): Promise<ChatThread> {
  const chatFetch = makeChatFetch(auth, baseUrl);
  const res = await chatFetch("/chat/threads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`AI engine ${res.status} ${res.statusText} on /chat/threads`);
  }
  return toChatThread((await res.json()) as EngineThread);
}

export async function deleteChatThread(auth: ChatAuth, baseUrl: string, threadId: ThreadId): Promise<void> {
  const chatFetch = makeChatFetch(auth, baseUrl);
  const res = await chatFetch(`/chat/threads/${threadId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(`AI engine ${res.status} ${res.statusText} on /chat/threads/${threadId}`);
  }
}
